import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Spin, Button, Space, Card, Typography, message } from 'antd';
import { DownloadOutlined, ArrowLeftOutlined, FileOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { FileViewer } from '@/components/FileManagement/doc-viewer';
import {
  isImage,
  isDoc,
  isMedia,
  isSupportIframe,
  isAudio
} from '@/components/FileManagement/utils';
import { buildFacilityPath, getFacilityContext } from '@/utils/route-context';

const { Title, Text } = Typography;

const FileSearchResultView = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const facilityContext = useSelector((state) => getFacilityContext(state));

  // 允许未编码的签名链接，优先从 searchParams 读取，若被截断则从原始 search 解析
  const getFileKey = () => {
    const fromParams = searchParams.get('url');
    if (fromParams && fromParams.includes('X-Amz-Signature')) {
      return decodeURIComponent(fromParams);
    }

    const search = searchParams.toString();
    const urlIndex = search.indexOf('url=');
    if (urlIndex === -1) return fromParams;

    const tail = search.slice(urlIndex + 4);
    const otherKeys = ['&name=', '&filename=', '&fileName='];
    const cutIndex = otherKeys
      .map(k => tail.indexOf(k))
      .filter(idx => idx !== -1)
      .reduce((min, idx) => (min === -1 ? idx : Math.min(min, idx)), -1);

    const rawUrl = cutIndex === -1 ? tail : tail.slice(0, cutIndex);
    return decodeURIComponent(rawUrl);
  };

  const fileKey = getFileKey();
  const name = searchParams.get('name');


  // 检查文件是否可预览
  const canPreview = (url) => {
    if (!url) return false;
    return isImage(url) || isDoc(url) || isMedia(url) || isSupportIframe(url) || isAudio(url);
  };

  // 获取文件扩展名
  const getFileExtension = (url) => {
    const regex = /\/[^\/?#]+\.(\w+)(?=[?#]|$)/i;
    const match = url?.match(regex);
    return match ? match[1].toUpperCase() : 'FILE';
  };

  // 获取文件名
  const getFileName = (url) => {
    if (!url) return 'Unknown File';
    const regex = /\/([^\/?#]+)(?=[?#]|$)/i;
    const match = url.match(regex);
    return match ? decodeURIComponent(match[1]) : 'Unknown File';
  };

  // 加载文件预览 URL
  // useEffect(() => {
  //   const loadFilePreview = () => {
  //     if (!fileKey) {
  //       setError('No file specified');
  //       return;
  //     }

  //     setLoading(true);
  //     setError(null);

  //     try {
  //       // fileKey is already the full signed S3 URL, use it directly
  //       const url = fileKey;
        
  //       setPreviewUrl(url);
  //       setFileInfo({
  //         name: getFileName(fileKey),
  //         extension: getFileExtension(fileKey),
  //         url: url,
  //         originalKey: fileKey,
  //         canPreview: canPreview(url)
  //       });
  //     } catch (err) {
  //       console.error('Failed to load file:', err);
  //       setError('Failed to load file. Please try again.');
  //       message.error('Failed to load file');
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   loadFilePreview();
  // }, [fileKey]);

  // 处理下载
  const handleDownloadClick = () => {
    
    try {
      // Use the signed URL directly for download
      
      const link = document.createElement('a');
      link.href = fileKey;
      link.setAttribute('target', '_blank');
      link.setAttribute('download', name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('Download started');
    } catch (err) {
      console.error('Download failed:', err);
      message.error('Failed to download file');
    }
  };

  // 返回上一页
  const handleGoBack = () => {
    const fallbackPath = buildFacilityPath('file-manage', facilityContext) || '/switch-view';

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(fallbackPath);
  };

  // if (loading) {
  //   return (
  //     <div style={{ 
  //       display: 'flex', 
  //       justifyContent: 'center', 
  //       alignItems: 'center', 
  //       height: '80vh' 
  //     }}>
  //       <Spin size="large" tip="Loading file..." />
  //     </div>
  //   );
  // }

  // if (error) {
  //   return (
  //     <div style={{ padding: '24px' }}>
  //       <Card>
  //         <div style={{ textAlign: 'center', padding: '40px 0' }}>
  //           <FileOutlined style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
  //           <Title level={4}>Error</Title>
  //           <Text type="secondary">{error}</Text>
  //           <div style={{ marginTop: 24 }}>
  //             <Button type="primary" icon={<ArrowLeftOutlined />} onClick={handleGoBack}>
  //               Go Back
  //             </Button>
  //           </div>
  //         </div>
  //       </Card>
  //     </div>
  //   );
  // }



  return (
    <div style={{ padding: '24px' }}>
      {/* 顶部操作栏 */}
      <Card style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleGoBack}>
              Back
            </Button>
            <div>
              <Text strong style={{ fontSize: 16 }}>{name}</Text>
              <br />
              {/* <Text type="secondary" style={{ fontSize: 12 }}>
                {fileInfo.extension} File
              </Text> */}
            </div>
          </Space>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />} 
            onClick={handleDownloadClick}
          >
            Download
          </Button>
        </Space>
      </Card>

      {/* 文件预览或信息显示 */}
      <Card>
        {canPreview(fileKey) ? (
          <div style={{ minHeight: '70vh' }}>
            <FileViewer 
              url={fileKey} 
              currentOpenFile={{
                isFolder: false,
                image: fileKey,
                name: name,
                tags_list: []
              }} 
            />
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <FileOutlined style={{ fontSize: 80, color: '#1890ff', marginBottom: 24 }} />
            <Title level={3}>{name}</Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              This file type cannot be previewed in the browser
            </Text>
            <div style={{ marginTop: 32 }}>
              <Button 
                type="primary" 
                size="large"
                icon={<DownloadOutlined />} 
                onClick={handleDownloadClick}
              >
                Download File
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default FileSearchResultView;

